import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { SkeletonLoader } from 'app/components/common/skeleton-loader/skeleton-loader';
import {
  ErrorText,
  ErrorTextContainer,
  FormContainer,
  LoadingContainer,
  PageContainer,
  StyledCaption,
  StyledConfirmButton,
  StyledDismissButton,
  StyledInput,
  TOGGLE_ACTIVE_COLOR
} from 'app/components/common/common-styles';
import { useTabsCtx } from 'app/components/management/apps/tabs-page-layout';
import { BodyText, FlexColumn, FlexRow, SvgIcon } from '@make-software/cspr-design';
import styled from 'styled-components';
import { updateApp } from 'app/console-api';
import { ErrorWindow } from 'app/components/common/error-window/error-window';
import NotFoundIcon from 'assets/logos/no-found.svg';
import { ErrorResult } from 'app/organizations-api/types';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { editAppValidationSchema } from 'app/components/management/apps/common/apps-validation-schema';
import { HttpError } from 'app/components/utils/http-error';
import { AUTH_TOKEN, customErrorMessage, CustomEvents, statusCode } from 'app/constants';
import { SIGN_IN_PATH } from 'app/route/paths';
import SuccessEntityWindow from 'app/components/common/success-entity-modal/success-entity-modal';
import CreateSuccessIcon from 'assets/logos/success-entity.svg';
import ErrorEntityWindow from 'app/components/common/error-entity-modal/error-entity-modal';
import { AppsCategoriesSelect } from 'app/components/management/apps/common/apps-categories-select';
import Switch from 'react-switch';
import { DropdownValue } from '@make-software/cspr-design/dist/lib/components/dropdown/dropdown';
import { AppContactsInput } from 'app/components/management/apps/common/app-contacts-input';

const StyledSeparator = styled.div<{ margin?: string }>(({ theme, margin }) =>
  theme.withMedia({
    borderBottom: '1px solid ' + theme.styleguideColors.borderPrimary,
    margin: margin ? `${margin} 0` : '16px 0'
  })
);

const StyledButtonRow = styled(FlexRow)(({ theme }) =>
  theme.withMedia({
    margin: '44px 0 40px 0'
  })
);

export const AppsEditDetails = () => {
  const { app, setApp } = useTabsCtx();
  if (!app) {
    return (
      <FormContainer>
        <LoadingContainer>
          <SkeletonLoader height={'40px'} />
          <SkeletonLoader height={'40px'} />
          <SkeletonLoader height={'40px'} />
          <SkeletonLoader height={'40px'} />
          <SkeletonLoader height={'40px'} />
          <SkeletonLoader height={'40px'} />
          <SkeletonLoader height={'40px'} />
        </LoadingContainer>
      </FormContainer>
    );
  }

  const [formData, setFormData] = useState({
    isActive: app.isActive,
    name: app.name,
    description: app.description,
    categories: app.categories.map((category) => {
      return { value: category, label: category };
    }),
    contacts: app.contacts,
    mau: app.mau,
    dau: app.dau
  });
  const [openSuccessModal, setOpenSuccessModal] = useState<boolean>(false);
  const [openStatusErrorModal, setOpenStatusErrorModal] = useState<boolean>(false);
  const [conflictError, setConflictError] = useState<string>('');
  const [unexpectedError, setUnexpectedError] = useState<ErrorResult | null>(null);

  const { setBreadcrumbs } = useTabsCtx();

  React.useEffect(() => {
    setBreadcrumbs([
      {
        label: 'Edit Details'
      }
    ]);
    return () => setBreadcrumbs([]);
  }, []);

  const {
    handleSubmit,
    setValue,
    clearErrors,
    formState: { errors }
  } = useForm({
    resolver: yupResolver(editAppValidationSchema),
    defaultValues: formData
  });

  const navigate = useNavigate();

  const handleInputChange = <K extends keyof typeof formData>(
    key: K,
    value: (typeof formData)[K]
  ) => {
    setFormData((prev) => ({
      ...prev,
      [key]: value
    }));
    // @ts-ignore
    setValue(key, value);
    // @ts-ignore
    clearErrors(key);
  };

  const handleSubmitData = async () => {
    updateApp(app.id, {
      ...formData,
      ...{
        categories: formData.categories.map((category) => category.value),
        contacts: formData.contacts
      }
    })
      .then((appData) => {
        setOpenSuccessModal(true);
        window.dispatchEvent(new CustomEvent(CustomEvents.appUpdated));
        setApp(appData);
      })
      .catch((error: any) => {
        if (error instanceof HttpError) {
          switch (error.statusCode) {
            case statusCode.validation_error:
              setUnexpectedError({
                code: error.statusCode.toString(),
                message: error.message
              });
              break;
            case statusCode.authentication_error:
              localStorage.removeItem(AUTH_TOKEN);
              window.location.href = SIGN_IN_PATH;
              return;
            case statusCode.conflict_error:
              setOpenStatusErrorModal(true);
              setConflictError(error.info?.error.description || error.message);
              break;
            case statusCode.unexpected_error:
              setUnexpectedError(customErrorMessage);
              break;
            default:
              setUnexpectedError(customErrorMessage);
              break;
          }
        } else {
          setUnexpectedError(customErrorMessage);
        }
        console.error('Failed to update app', error);
      });
  };

  const sentData = () => {
    handleSubmit(handleSubmitData)();
  };

  const handleOnDismiss = () => {
    clearErrors();
    navigate(-1);
  };

  const handleCloseSuccessModal = () => {
    setOpenSuccessModal(false);
    setOpenStatusErrorModal(false);
  };

  if (unexpectedError) {
    return (
      <ErrorWindow
        withModal
        title={unexpectedError.code}
        information={unexpectedError.message}
        icon={<SvgIcon src={NotFoundIcon} width={219} height={125} />}
      />
    );
  }

  return (
    <PageContainer>
      <FlexRow justify={'center'} align={'center'}>
        <FormContainer>
          <SuccessEntityWindow
            dismissLabel={'Close'}
            onDismiss={handleCloseSuccessModal}
            isOpen={openSuccessModal}
            icon={<SvgIcon src={CreateSuccessIcon} width={195} height={120} />}
            title="App has been updated"
            information="The app was successfully updated."
          />
          <ErrorEntityWindow
            dismissLabel={'Close'}
            onDismiss={handleCloseSuccessModal}
            isOpen={openStatusErrorModal}
            title={conflictError}
            information={
              <>
                There are some <b>active offers</b> related to this App. They must be deactivated
                first.
              </>
            }
          />
          <StyledCaption />
          <StyledSeparator />
          <FlexRow justify="space-between">
            <BodyText size={1}>Active Status</BodyText>
            <Switch
              checked={formData.isActive}
              onChange={() => handleInputChange('isActive', !formData.isActive)}
              onColor={TOGGLE_ACTIVE_COLOR}
              checkedIcon={false}
              uncheckedIcon={false}
            />
          </FlexRow>
          <StyledSeparator />
          <FlexColumn gap={10}>
            <StyledInput
              required
              label={<BodyText size={1}>Name</BodyText>}
              placeholder={'App name'}
              value={formData.name}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                handleInputChange('name', e.target.value)
              }
            />
            {errors?.name?.message && (
              <ErrorTextContainer justify={'flex-start'}>
                <ErrorText size={3}>{errors?.name?.message}</ErrorText>
              </ErrorTextContainer>
            )}
            <StyledInput
              label={<BodyText size={1}>Description</BodyText>}
              placeholder={'Describe your app here'}
              value={formData.description}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                handleInputChange('description', e.target.value)
              }
            />
            {errors?.description?.message && (
              <ErrorTextContainer justify={'flex-start'}>
                <ErrorText size={3}>{errors?.description?.message}</ErrorText>
              </ErrorTextContainer>
            )}
            <FlexColumn gap={4}>
              <BodyText size={1}>Categories</BodyText>
              <AppsCategoriesSelect
                categories={formData.categories}
                handleInputChange={(e: any) =>
                  handleInputChange('categories', e as unknown as DropdownValue[])
                }
              />
              {errors?.categories?.message && (
                <ErrorTextContainer justify={'flex-start'}>
                  <ErrorText size={3}>{errors?.categories?.message}</ErrorText>
                </ErrorTextContainer>
              )}
            </FlexColumn>
            <AppContactsInput
              contacts={formData.contacts}
              onChange={(contacts) => handleInputChange('contacts', contacts)}
            />
            <StyledInput
              label={<BodyText size={1}>Monthly Active Users (MAU)</BodyText>}
              value={formData.mau}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                handleInputChange('mau', Number(e.target.value))
              }
            />
            {errors?.mau?.message && (
              <ErrorTextContainer justify={'flex-start'}>
                <ErrorText size={3}>{errors?.mau?.message}</ErrorText>
              </ErrorTextContainer>
            )}
            <StyledInput
              label={<BodyText size={1}>Daily Active Users (DAU)</BodyText>}
              value={formData.dau}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                handleInputChange('dau', Number(e.target.value))
              }
            />
            {errors?.dau?.message && (
              <ErrorTextContainer justify={'flex-start'}>
                <ErrorText size={3}>{errors?.dau?.message}</ErrorText>
              </ErrorTextContainer>
            )}
          </FlexColumn>

          <StyledButtonRow justify="space-between" gap={10}>
            <StyledDismissButton color={'secondaryBlue'} onClick={handleOnDismiss}>
              Cancel
            </StyledDismissButton>
            <StyledConfirmButton onClick={sentData}>Save</StyledConfirmButton>
          </StyledButtonRow>
        </FormContainer>
      </FlexRow>
    </PageContainer>
  );
};
